import { FormEvent, useState, useEffect } from "react";
import { useProfile } from "@/hooks/server-state/use-profile";
import MoonLoader from "react-spinners/MoonLoader";
import { Trash2 } from "lucide-react";
import SaveButton from "../_components/save-button";
import { useMutation } from "@tanstack/react-query";
import { editNewsProfile, EditNewsItemType, EditNewsType } from "@/api/profile";

// Loading component
const Loading = () => (
  <div className="flex h-screen w-full items-center justify-center bg-white">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

// Repeater component for News
type NewsItem = EditNewsItemType;

type NewsRepeaterProps = {
  items: NewsItem[];
  onValuesChange: (values: NewsItem[]) => void;
};

const NewsRepeater = ({ items, onValuesChange }: NewsRepeaterProps) => {
  const handleChange = (index: number, field: "title" | "content", value: string) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onValuesChange(updated);
  };

  const addItem = () => {
    const updated = [...items, { title: "", content: "" }];
    onValuesChange(updated);
  };

  const removeItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    onValuesChange(updated);
  };

  return (
    <div className="space-y-6">
      {items.map((item, index) => (
        <div key={index} className="flex flex-col gap-2 border p-4 rounded relative">
          <select
            value={item.title}
            onChange={(e) => handleChange(index, "title", e.target.value)}
            className="border p-2 rounded w-1/3"
          >
            <option value="">انتخاب کنید...</option>
            <option value="پذیرش پژوهشگر پسا دکترا">پذیرش پژوهشگر پسا دکترا</option>
            <option value="پذیرش دانشجوی دکتری">پذیرش دانشجوی دکتری</option>
            <option value="پذیرش دانشجوی کارشناسی ارشد">پذیرش دانشجوی کارشناسی ارشد</option>
            <option value="پذیرش پژوهشگر همکار">پذیرش پژوهشگر همکار</option>
          </select>

          <textarea
            placeholder="Content"
            value={item.content}
            onChange={(e) => handleChange(index, "content", e.target.value)}
            className="border p-2 rounded w-full min-h-[100px]"
          />
          <div className="w-full text-left flex gap-2 justify-between">
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-700 ">این خبر بصورت پیش فرض به مدت 2 ماه بر روی سامانه معتبر خواهد بود در صورت نیاز زمان اتمام را مشخص نمایید</p>
              <input type="date" className="border p-2 rounded w-448" />
            </div>
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="text-red-500 border-red-500 px-2  border p-1 rounded-full hover:bg-red-500 hover:text-white flex gap-2 items-center"
            >
              حذف خبر
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="bg-teal-500 text-white px-4 py-2 rounded"
      >
        + افزودن خبر جدید
      </button>
    </div>
  );
};

// Main component
const EditProfileNewsMe = () => {
  const { data: profileData, isLoading: isProfileLoading, refetch } = useProfile();
  const [editData, setEditData] = useState<EditNewsType>({ news: [] });

  const [isInitialized, setIsInitialized] = useState(false);

  // بروزرسانی editData فقط در اولین لود موفق پروفایل
  useEffect(() => {
    if (profileData?.news && !isInitialized) {
      setEditData({
        news: profileData.news.map((n) => ({ title: n.title, content: n.content })),
      });
      setIsInitialized(true);
    }
  }, [profileData, isInitialized]);

  // mutation برای ذخیره خبرها
  const mutation = useMutation({
    mutationFn: (input: EditNewsType) => editNewsProfile(input),
    onSuccess: async () => {
      await refetch();
      setIsInitialized(false);
    },
  });

  if (isProfileLoading) return <Loading />;

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!profileData) return;

    const payload = {
      id: profileData.id,
      news: editData.news || [],
    };

    mutation.mutate(payload);
  };

  return (
    <>
      <div className="p-4">
        <div className="mr-4">
          <h1 className="text-xl font-bold mb-4">اخبار پذیرش دانشجو یا پژوهشگر</h1>
          <p className="text-gray-500">لطفا اخبار مربوط به پذیرش دانشجو یا پژوهشگر را در این بخش وارد نمایید.</p>
          <p className="text-gray-500">با توجه به محدودیت تعداد کلمات اخبار لطفا در صورت نیاز به اطلاعات بیشتر لینک پذیرش را در خبر درج نمایید. </p>
        </div>
        <NewsRepeater
          items={editData.news}
          onValuesChange={(values) => setEditData((prev) => ({ ...prev, news: values }))}
        />
      </div>
      <form onSubmit={handleSave}>
        <SaveButton />
      </form>
    </>
  );
};

export default EditProfileNewsMe;
